import { useMemo, useState, type ReactNode } from 'react';
import {
  addBeat,
  addCase,
  addOption,
  addOutcome,
  addTable,
  beatOrder,
  remove,
  removeOption,
  rename,
  type RenameKind,
} from '../../lib/story/chainEdit';
import { analyzeChain, deepest, type BeatNode, type ToneMix } from '../../lib/story/chainTree';
import { effectSummary } from '../../lib/story/effects';
import { IMPLICIT_ROLES } from '../../lib/story/template';
import {
  BEAT_CHAIN_STATUSES,
  CAST_BINDS,
  HIDDEN_VAR_TYPES,
  OUTCOME_TONES,
  type BeatChainDef,
  type BeatDef,
  type CastRoleDef,
  type CellDef,
  type HiddenVarDef,
  type OptionDef,
  type OutcomeDef,
  type OutcomeTableCase,
  type TellDef,
} from '../../lib/story/types';
import { newPanel, storyRulesDefaults } from '../../lib/storyDefaults';
import { issuesUnder, type FieldErrors } from '../../lib/validate';
import { storyRules } from '../../lib/validate/beats';
import { condText } from '../../lib/validate/conds';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
  inputCls,
  smallBtn,
} from '../forms/fields';
import { useContentBundle } from '../forms/refs';
import type { FormProps } from '../forms/types';
import { BranchPreview } from './BranchPreview';
import { CellEditor, type CellContext } from './CellEditor';
import { ConditionPicker } from './ConditionPicker';
import { EffectListEditor } from './EffectEditor';
import { relErrors } from './relErrors';
import { SimulatePanel } from './SimulatePanel';
import { TONE_COLORS } from '../../lib/story/ui';
import { TemplateInput, ToneMixBar } from './StoryBits';

type Sel =
  | { kind: 'chain' }
  | { kind: 'strings' }
  | { kind: 'beat'; id: string }
  | { kind: 'option'; beat: string; index: number }
  | { kind: 'table'; id: string }
  | { kind: 'outcome'; id: string };

const opts = (list: readonly string[]) => list.map((v) => ({ value: v, label: v }));
const pctMix = (
  m: Required<NonNullable<ReturnType<typeof storyRulesDefaults>['tone']['targetMix']>>,
): ToneMix => ({
  disaster: m.disaster ?? 0,
  bad: m.bad ?? 0,
  mixed: m.mixed ?? 0,
  good: m.good ?? 0,
  windfall: m.windfall ?? 0,
});

/** Beat id → the shallowest depth it appears at, and whether any path to it is too deep or a cycle. */
function beatDepths(root: BeatNode | undefined) {
  const out = new Map<string, { depth: number; tooDeep: boolean; cycle: boolean }>();
  const visit = (n: BeatNode | undefined) => {
    if (!n) return;
    const cur = out.get(n.beatId);
    out.set(n.beatId, {
      depth: cur ? Math.min(cur.depth, n.depth) : n.depth,
      tooDeep: (cur?.tooDeep ?? false) || n.tooDeep,
      cycle: (cur?.cycle ?? false) || n.cycle,
    });
    if (n.cycle) return;
    n.options.forEach((o) => o.outcomes.forEach((x) => visit(x.next)));
  };
  visit(root);
  return out;
}

/**
 * The beat editor (engine.html §5 "Admin: the beat editor"): a tree outline (beats → options → outcomes, depth on every beat, red
 * over the limit), a node editor for the selection (chain settings, beat with cells and choice, option with its table's cases,
 * outcome with tone, cells and typed effects, strings), and above them the branch preview, the checks, the reach-weighted tone mix
 * against `story.tone.targetMix`, and Simulate rolls. A path deeper than the limit can't get a new beat and fails validation, so it
 * can't publish.
 */
export function BeatChainForm({ value, onChange, errors }: FormProps<BeatChainDef>) {
  const chain = value;
  const bundle = useContentBundle();
  const rules = storyRules(bundle) ?? storyRulesDefaults();
  const maxDepth = Math.min(4, Math.max(1, rules.maxDepth ?? 4));
  const target = pctMix({
    ...storyRulesDefaults().tone.targetMix,
    ...rules.tone?.targetMix,
  } as never);
  const [sel, setSel] = useState<Sel>({ kind: 'chain' });
  const [showSim, setShowSim] = useState(false);
  const analysis = useMemo(() => analyzeChain(chain, { maxDepth }), [chain, maxDepth]);
  const depths = useMemo(() => beatDepths(analysis.root), [analysis]);

  const roles = [...(chain.cast ?? []).map((c) => c.role ?? '').filter(Boolean), ...IMPLICIT_ROLES];
  const roleSet = new Set(roles);
  const vars = [
    ...(chain.hidden ?? []).map((h) => h.id ?? ''),
    ...(chain.tells ?? []).map((t) => t.id ?? ''),
  ].filter(Boolean);
  const cellCtx: CellContext = {
    roles,
    vars,
    strings: chain.strings ?? {},
    setString: (k, v) => onChange({ ...chain, strings: { ...(chain.strings ?? {}), [k]: v } }),
  };
  const setBeat = (id: string, b: BeatDef) =>
    onChange({ ...chain, beats: { ...(chain.beats ?? {}), [id]: b } });
  const setTable = (id: string, t: OutcomeTableCase[]) =>
    onChange({ ...chain, tables: { ...(chain.tables ?? {}), [id]: t } });
  const setOutcome = (id: string, o: OutcomeDef) =>
    onChange({ ...chain, outcomes: { ...(chain.outcomes ?? {}), [id]: o } });
  const doRename = (kind: RenameKind, from: string, to: string) => {
    const next = rename(chain, kind, from, to);
    if (next === chain) return false;
    onChange(next);
    if (sel.kind === kind && (sel as { id: string }).id === from) setSel({ kind, id: to } as Sel);
    return true;
  };
  const errs = (base: string) => issuesUnder(errors, base);
  const dot = (base: string) => {
    const n = errs(base).filter((i) => i.severity === 'error').length;
    return n ? (
      <span className="ml-1 font-semibold text-blam" title={`${n} error(s)`}>
        ● {n}
      </span>
    ) : null;
  };

  // ----- the outline -----
  const order = beatOrder(chain);
  const usedTables = new Set(
    Object.values(chain.beats ?? {}).flatMap((b) =>
      (b.choice?.options ?? []).map((o) => o.table ?? ''),
    ),
  );
  const pickedOutcomes = new Set([
    ...Object.values(chain.tables ?? {}).flatMap((cs) =>
      cs.flatMap((k) => (k.pick ?? []).map((p) => p.outcome ?? '')),
    ),
    ...Object.values(chain.beats ?? {}).map((b) => b.outcome ?? ''),
  ]);
  const item = (s: Sel, label: ReactNode, base: string, extra = '') => {
    const active = JSON.stringify(s) === JSON.stringify(sel);
    return (
      <button
        type="button"
        onClick={() => setSel(s)}
        className={`block w-full truncate rounded px-1 text-left ${active ? 'bg-benday/40 font-semibold' : 'hover:bg-caption/30'} ${extra}`}
      >
        {label}
        {dot(base)}
      </button>
    );
  };
  const outcomeChip = (id: string) => {
    const o = chain.outcomes?.[id];
    const tone = (o?.tone ?? 'mixed') as keyof typeof TONE_COLORS;
    return (
      <span className="inline-flex items-center gap-1">
        <span
          className="inline-block h-2 w-2 shrink-0"
          style={{ background: TONE_COLORS[tone] ?? '#fff' }}
        />
        <span className={o ? '' : 'text-blam'}>{id || '(none)'}</span>
        {o?.effects?.length ? (
          <span className="text-bruise">· {o.effects.map((e) => e.type).join(', ')}</span>
        ) : null}
      </span>
    );
  };

  const outline = (
    <nav aria-label="Chain outline" className="text-xs">
      {item({ kind: 'chain' }, <span className="font-bold">CHAIN · {chain.id}</span>, 'cast')}
      <p className="px-1 text-bruise">
        cast: {(chain.cast ?? []).map((c) => c.role).join(', ') || '–'}
        <br />
        hidden: {(chain.hidden ?? []).map((h) => h.id).join(', ') || '–'}
        <br />
        tells: {(chain.tells ?? []).map((t) => t.id).join(', ') || '–'}
      </p>
      {item({ kind: 'strings' }, `strings (${Object.keys(chain.strings ?? {}).length})`, 'strings')}
      <ul className="mt-1 space-y-1">
        {order.map((id) => {
          const b = chain.beats?.[id];
          const d = depths.get(id);
          const reachable = !!d;
          const bad = !!d?.tooDeep || !!d?.cycle || !reachable;
          const desc = b?.choice
            ? `${b.choice.timerS != null ? `${b.choice.timerS} s` : 'no timer'}${b.choice.timeout ? `, timeout ${b.choice.timeout}` : ''}`
            : 'cutscene';
          return (
            <li key={id} data-testid={`outline-beat-${id}`} data-bad={bad ? 'true' : undefined}>
              {item(
                { kind: 'beat', id },
                <>
                  ▾ <span className="font-mono">{id}</span> ({chain.root === id ? 'root, ' : ''}
                  {desc}){' '}
                  <span className={bad ? 'font-semibold text-blam' : 'text-bruise'}>
                    {reachable ? `d${d!.depth}` : 'unreachable'}
                    {d?.tooDeep ? ` > ${maxDepth}` : ''}
                    {d?.cycle ? ' cycle' : ''}
                  </span>
                </>,
                `beats.${id}`,
                bad ? 'text-blam' : '',
              )}
              <ul className="ml-3">
                {b?.choice
                  ? (b.choice.options ?? []).map((o, i) => (
                      <li key={i}>
                        {item(
                          { kind: 'option', beat: id, index: i },
                          <>
                            ▸ {o.id} → <span className="font-mono">{o.table}</span>
                          </>,
                          `beats.${id}.choice.options.${i}`,
                        )}
                        <ul className="ml-3">
                          {[
                            ...new Set(
                              (chain.tables?.[o.table ?? ''] ?? []).flatMap((k) =>
                                (k.pick ?? []).map((p) => p.outcome ?? ''),
                              ),
                            ),
                          ].map((oid) => (
                            <li key={oid}>
                              {item(
                                { kind: 'outcome', id: oid },
                                outcomeChip(oid),
                                `outcomes.${oid}`,
                              )}
                              {chain.outcomes?.[oid]?.next && (
                                <button
                                  type="button"
                                  className="ml-4 text-bruise underline"
                                  onClick={() =>
                                    setSel({ kind: 'beat', id: chain.outcomes![oid]!.next! })
                                  }
                                >
                                  → {chain.outcomes[oid]!.next}
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))
                  : b?.outcome && (
                      <li>
                        {item(
                          { kind: 'outcome', id: b.outcome },
                          outcomeChip(b.outcome),
                          `outcomes.${b.outcome}`,
                        )}
                      </li>
                    )}
              </ul>
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex flex-wrap gap-1">
        <button
          type="button"
          className={smallBtn}
          onClick={() => {
            const [next, id] = addBeat(chain);
            onChange(next);
            setSel({ kind: 'beat', id });
          }}
        >
          Add beat
        </button>
        <button
          type="button"
          className={smallBtn}
          onClick={() => {
            const [next, id] = addOutcome(chain);
            onChange(next);
            setSel({ kind: 'outcome', id });
          }}
        >
          Add outcome
        </button>
      </div>
      {(Object.keys(chain.tables ?? {}).some((t) => !usedTables.has(t)) ||
        Object.keys(chain.outcomes ?? {}).some((o) => !pickedOutcomes.has(o))) && (
        <div className="mt-2">
          <p className="font-semibold">Unused</p>
          {Object.keys(chain.tables ?? {})
            .filter((t) => !usedTables.has(t))
            .map((t) => (
              <div key={t}>
                {item(
                  { kind: 'table', id: t },
                  <>
                    table <span className="font-mono">{t}</span>
                  </>,
                  `tables.${t}`,
                )}
              </div>
            ))}
          {Object.keys(chain.outcomes ?? {})
            .filter((o) => !pickedOutcomes.has(o))
            .map((o) => (
              <div key={o}>{item({ kind: 'outcome', id: o }, outcomeChip(o), `outcomes.${o}`)}</div>
            ))}
        </div>
      )}
    </nav>
  );

  // ----- the node editor -----
  let editor: ReactNode = null;
  if (sel.kind === 'chain')
    editor = (
      <ChainEditor chain={chain} onChange={onChange} errors={errors} roles={roles} vars={vars} />
    );
  else if (sel.kind === 'strings')
    editor = <StringsEditor chain={chain} onChange={onChange} roles={roleSet} errors={errors} />;
  else if (sel.kind === 'beat') {
    const b = chain.beats?.[sel.id];
    editor = b ? (
      <BeatEditor
        id={sel.id}
        beat={b}
        chain={chain}
        onChange={(nb) => setBeat(sel.id, nb)}
        onRename={(to) => doRename('beat', sel.id, to)}
        onRoot={() => onChange({ ...chain, root: sel.id })}
        onDelete={() => {
          if (!confirm(`Delete beat '${sel.id}'? Outcomes leading to it are cleared.`)) return;
          onChange(remove(chain, 'beat', sel.id));
          setSel({ kind: 'chain' });
        }}
        onAddOption={() => onChange(addOption(chain, sel.id))}
        onRemoveOption={(i) => onChange(removeOption(chain, sel.id, i))}
        onSelect={setSel}
        errors={errors}
        cellCtx={cellCtx}
        roles={roles}
        vars={vars}
      />
    ) : (
      <p className="text-blam">Beat '{sel.id}' doesn't exist.</p>
    );
  } else if (sel.kind === 'option') {
    const b = chain.beats?.[sel.beat];
    const o = b?.choice?.options?.[sel.index];
    editor =
      b && o ? (
        <>
          <OptionEditor
            option={o}
            base={`beats.${sel.beat}.choice.options.${sel.index}`}
            chain={chain}
            onChange={(no) =>
              setBeat(sel.beat, {
                ...b,
                choice: {
                  ...b.choice!,
                  options: (b.choice!.options ?? []).map((x, j) => (j === sel.index ? no : x)),
                },
              })
            }
            onNewTable={() => {
              const [next, tid] = addTable(chain, Object.keys(chain.outcomes ?? {})[0] ?? '');
              const nb = next.beats![sel.beat]!;
              nb.choice!.options![sel.index]!.table = tid;
              onChange(next);
            }}
            errors={errors}
            roles={roles}
            vars={vars}
          />
          {o.table && chain.tables?.[o.table] && (
            <TableEditor
              id={o.table}
              chain={chain}
              onChange={(t) => setTable(o.table!, t)}
              onAddCase={() => onChange(addCase(chain, o.table!))}
              onNewOutcome={(caseIdx, pickIdx) => {
                const [next, oid] = addOutcome(chain, `o${sel.beat}${o.id}_`);
                const pick = next.tables![o.table!]![caseIdx]!.pick![pickIdx]!;
                pick.outcome = oid;
                onChange(next);
              }}
              onRename={(to) => doRename('table', o.table!, to)}
              onSelectOutcome={(oid) => setSel({ kind: 'outcome', id: oid })}
              errors={errors}
              roles={roles}
              vars={vars}
            />
          )}
        </>
      ) : (
        <p className="text-blam">That option doesn't exist any more.</p>
      );
  } else if (sel.kind === 'table') {
    const t = chain.tables?.[sel.id];
    editor = t ? (
      <TableEditor
        id={sel.id}
        chain={chain}
        onChange={(nt) => setTable(sel.id, nt)}
        onAddCase={() => onChange(addCase(chain, sel.id))}
        onNewOutcome={(caseIdx, pickIdx) => {
          const [next, oid] = addOutcome(chain);
          next.tables![sel.id]![caseIdx]!.pick![pickIdx]!.outcome = oid;
          onChange(next);
        }}
        onRename={(to) => doRename('table', sel.id, to)}
        onDelete={() => {
          onChange(remove(chain, 'table', sel.id));
          setSel({ kind: 'chain' });
        }}
        onSelectOutcome={(oid) => setSel({ kind: 'outcome', id: oid })}
        errors={errors}
        roles={roles}
        vars={vars}
      />
    ) : (
      <p className="text-blam">Table '{sel.id}' doesn't exist.</p>
    );
  } else if (sel.kind === 'outcome') {
    const o = chain.outcomes?.[sel.id];
    const depthHere = (() => {
      // The deepest beat that picks this outcome: a new beat after it would be one choice deeper.
      let d = 0;
      for (const [bid, b] of Object.entries(chain.beats ?? {})) {
        const picks = b.choice
          ? (b.choice.options ?? []).flatMap((op) =>
              (chain.tables?.[op.table ?? ''] ?? []).flatMap((k) =>
                (k.pick ?? []).map((p) => p.outcome),
              ),
            )
          : [b.outcome];
        if (picks.includes(sel.id)) d = Math.max(d, depths.get(bid)?.depth ?? 0);
      }
      return d;
    })();
    editor = o ? (
      <OutcomeEditor
        id={sel.id}
        outcome={o}
        chain={chain}
        onChange={(no) => setOutcome(sel.id, no)}
        onRename={(to) => doRename('outcome', sel.id, to)}
        onDelete={() => {
          onChange(remove(chain, 'outcome', sel.id));
          setSel({ kind: 'chain' });
        }}
        canAddBeat={depthHere < maxDepth}
        onAddNextBeat={() => {
          const [next, bid] = addBeat(chain, { outcomeId: sel.id });
          onChange(next);
          setSel({ kind: 'beat', id: bid });
        }}
        onSelectBeat={(bid) => setSel({ kind: 'beat', id: bid })}
        errors={errors}
        cellCtx={cellCtx}
        roles={roles}
        maxDepth={maxDepth}
      />
    ) : (
      <p className="text-blam">Outcome '{sel.id}' doesn't exist.</p>
    );
  }

  // ----- preview, checks and tone mix -----
  const depth = deepest(analysis.root);
  const maxRolls = rules.maxRollSheetEntries ?? 120;
  const warnings = Object.values(errors)
    .flat()
    .filter((i) => i.severity === 'warning');
  const flagged: { beat: string; option: string; note: string }[] = [];
  const optionMixes: { key: string; label: string; mix: ToneMix }[] = [];
  const walk = (n: BeatNode | undefined) => {
    if (!n || n.cycle) return;
    for (const o of n.options) {
      if (!o.cutscene) {
        optionMixes.push({ key: o.key, label: `${n.beatId} ─${o.optionId}─`, mix: o.toneMix });
        if (!o.hasGoodPath) flagged.push({ beat: n.beatId, option: o.optionId, note: o.toneNote });
      }
      o.outcomes.forEach((x) => walk(x.next));
    }
  };
  walk(analysis.root);

  return (
    <div className="text-sm">
      <section
        className="mb-3 rounded border-2 border-ink bg-white p-2"
        aria-label="Branch preview"
      >
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xs font-bold uppercase tracking-wide">Branch preview</h3>
          <span className="text-xs text-bruise">
            expected values; Simulate rolls for the server's numbers
          </span>
        </div>
        <BranchPreview
          root={analysis.root}
          onSelect={(n) => {
            const key = n.id.slice(2);
            if (n.kind === 'beat') setSel({ kind: 'beat', id: key.split('/').pop()! });
            if (n.kind === 'outcome')
              setSel({ kind: 'outcome', id: key.slice(key.lastIndexOf(':') + 1) });
            if (n.kind === 'option') {
              const parts = key.split('/');
              const optId = parts.pop()!;
              const beatId = parts.pop()!;
              const i = (chain.beats?.[beatId]?.choice?.options ?? []).findIndex(
                (o) => o.id === optId,
              );
              if (i >= 0) setSel({ kind: 'option', beat: beatId, index: i });
            }
          }}
        />
        <div className="mt-2 grid gap-3 text-xs lg:grid-cols-2">
          <ul aria-label="Checks" className="space-y-0.5">
            <li className={depth > maxDepth ? 'font-semibold text-blam' : ''}>
              {depth > maxDepth ? '✗' : '✓'} depth {depth} / {maxDepth}
            </li>
            <li className={analysis.rollSheetEntries > maxRolls ? 'font-semibold text-blam' : ''}>
              {analysis.rollSheetEntries > maxRolls ? '✗' : '✓'} roll sheet{' '}
              {analysis.rollSheetEntries} / {maxRolls}
            </li>
            {analysis.unreachableBeats.length > 0 && (
              <li className="text-blam">✗ unreachable: {analysis.unreachableBeats.join(', ')}</li>
            )}
            {analysis.cycles.length > 0 && (
              <li className="text-blam">✗ cycle through {analysis.cycles.join(', ')}</li>
            )}
            <li className={analysis.hasGoodOutcome ? '' : 'font-semibold text-blam'}>
              {analysis.hasGoodOutcome
                ? '✓ has a good or windfall outcome'
                : '✗ no good or windfall outcome'}
            </li>
            {flagged.map((f) => (
              <li
                key={`${f.beat}/${f.option}`}
                className={f.note ? 'text-bruise' : 'font-semibold text-blam'}
              >
                ⚠ {f.beat} {f.option} has no good path
                {f.note ? ` · toneNote: "${f.note}"` : ' · needs a toneNote'}
              </li>
            ))}
            {warnings.map((w, i) => (
              <li key={i} className="text-bruise">
                ⚠ {w.message}
              </li>
            ))}
            {!warnings.length && (
              <li className="text-bruise">⚠ none dominant · tells not certain</li>
            )}
          </ul>
          <div className="space-y-2">
            <ToneMixBar
              label="Tone mix (reach-weighted) vs target"
              mix={analysis.toneMix}
              target={target}
            />
            <details>
              <summary className="cursor-pointer">Per option</summary>
              {optionMixes.map((o) => (
                <ToneMixBar key={o.key} label={o.label} mix={o.mix} height={8} />
              ))}
            </details>
          </div>
        </div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className={smallBtn}
            onClick={() => setShowSim(!showSim)}
            aria-expanded={showSim}
          >
            Simulate rolls
          </button>
        </div>
        {showSim && (
          <div className="mt-2">
            <SimulatePanel chain={chain} />
          </div>
        )}
      </section>
      <div className="grid gap-3 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <div className="rounded border-2 border-ink bg-white p-2 lg:max-h-[80vh] lg:overflow-y-auto">
          {outline}
        </div>
        <div>{editor}</div>
      </div>
    </div>
  );
}

// ======================================================================================================================================

/** An id field that renames on blur or Enter (every reference follows); a taken or blank id is refused inline. */
function RenameField({
  label,
  id,
  onRename,
}: {
  label: string;
  id: string;
  onRename: (to: string) => boolean;
}) {
  const [text, setText] = useState(id);
  const [seen, setSeen] = useState(id);
  const [problem, setProblem] = useState<string | null>(null);
  if (id !== seen) {
    setSeen(id);
    setText(id);
    setProblem(null);
  }
  const commit = () => {
    if (text === id) return;
    if (!/^[a-z][A-Za-z0-9_]*$/.test(text)) {
      setProblem('Ids start with a lower-case letter: letters, digits and _.');
      return;
    }
    if (!onRename(text)) setProblem(`'${text}' is taken.`);
    else setProblem(null);
  };
  return (
    <Field label={label} hint="Renames everywhere on Enter or when you leave the field.">
      {(fid) => (
        <>
          <input
            id={fid}
            className={`${inputCls} font-mono`}
            value={text}
            aria-invalid={problem !== null}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === 'Enter' && commit()}
          />
          {problem && (
            <p role="alert" className="text-xs text-blam">
              {problem}
            </p>
          )}
        </>
      )}
    </Field>
  );
}

function ChainEditor({
  chain,
  onChange,
  errors,
  roles,
  vars,
}: {
  chain: BeatChainDef;
  onChange: (c: BeatChainDef) => void;
  errors: FieldErrors;
  roles: string[];
  vars: string[];
}) {
  const b = useContentBundle();
  const set = (patch: Partial<BeatChainDef>) => onChange({ ...chain, ...patch });
  const hiddenIds = (chain.hidden ?? []).map((h) => h.id ?? '');
  return (
    <>
      <Fieldset legend="Chain">
        <TextField
          label="Id"
          mono
          value={chain.id}
          issues={errors.id}
          onChange={(v) => set({ id: v })}
        />
        <TextField
          label="Title"
          value={chain.title}
          issues={errors.title}
          onChange={(v) => set({ title: v })}
        />
        <SelectField
          label="Status"
          value={chain.status ?? 'live'}
          options={opts(BEAT_CHAIN_STATUSES)}
          issues={errors.status}
          onChange={(v) => set({ status: v ?? 'live' })}
        />
        <SelectField
          label="Root beat"
          value={chain.root ?? ''}
          options={opts(Object.keys(chain.beats ?? {}))}
          issues={errors.root}
          onChange={(v) => set({ root: v ?? '' })}
        />
        <StringListEditor
          label="Tags"
          hint="For story sources to filter by: companion, echo, trade, stranger."
          value={chain.tags}
          onChange={(v) => set({ tags: v })}
          errors={errors}
          base="tags"
        />
        <ConditionPicker
          label="Eligible (server, at issue)"
          value={chain.eligible}
          onChange={(c) => set({ eligible: c })}
          hints={{ vars, roles, allowRaid: false }}
          issues={relErrors(errors, 'eligible')}
        />
      </Fieldset>
      <Fieldset legend="Cast">
        <ListEditor
          label="Roles (player and leader are always there)"
          items={chain.cast ?? []}
          onChange={(v) => set({ cast: v })}
          what="role"
          make={(): CastRoleDef => ({
            role: '',
            bind: 'squad_member',
            exclude: ['player', 'leader'],
            prefer: '',
          })}
          row={(r, i, put) => (
            <div className="grid gap-1 sm:grid-cols-3">
              <TextField
                label="Role"
                mono
                value={r.role}
                issues={errors[`cast.${i}.role`]}
                onChange={(v) => put({ ...r, role: v })}
              />
              <SelectField
                label="Bind"
                value={r.bind ?? 'squad_member'}
                options={opts(CAST_BINDS)}
                issues={errors[`cast.${i}.bind`]}
                onChange={(v) => put({ ...r, bind: v ?? 'squad_member' })}
              />
              {(r.bind ?? 'squad_member') === 'squad_member' && (
                <>
                  <TextField
                    label="Exclude (comma)"
                    mono
                    value={(r.exclude ?? []).join(', ')}
                    onChange={(v) =>
                      put({
                        ...r,
                        exclude: v
                          .split(',')
                          .map((x) => x.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                  <TextField
                    label="Prefer"
                    mono
                    value={r.prefer}
                    onChange={(v) => put({ ...r, prefer: v })}
                    hint="lowest_hp, …"
                  />
                </>
              )}
              {r.bind === 'npc' && (
                <>
                  <TextField
                    label="Archetype"
                    mono
                    value={r.archetype}
                    issues={errors[`cast.${i}.archetype`]}
                    onChange={(v) => put({ ...r, archetype: v })}
                  />
                  <SelectField
                    label="Or NPC"
                    value={r.npcId ?? ''}
                    none="(by archetype)"
                    options={(b.npcs ?? []).map((n) => ({
                      value: n?.id ?? '',
                      label: `${n?.name ?? ''} (${n?.id ?? ''})`,
                    }))}
                    issues={errors[`cast.${i}.npcId`]}
                    onChange={(v) => put({ ...r, npcId: v ?? '' })}
                  />
                </>
              )}
              {r.bind === 'ledger' && (
                <>
                  <SelectField
                    label="Ledger chain"
                    value={r.ledgerChain ?? ''}
                    options={(b.beatChains ?? []).map((c) => ({
                      value: c?.id ?? '',
                      label: c?.id ?? '',
                    }))}
                    issues={errors[`cast.${i}.ledgerChain`]}
                    onChange={(v) => put({ ...r, ledgerChain: v ?? '' })}
                  />
                  <TextField
                    label="Ledger role"
                    mono
                    value={r.ledgerRole}
                    onChange={(v) => put({ ...r, ledgerRole: v })}
                  />
                </>
              )}
              {r.bind === 'ally' && (
                <SelectField
                  label="Ally"
                  value={r.allyId ?? ''}
                  options={(b.allies ?? []).map((a) => ({
                    value: a?.id ?? '',
                    label: `${a?.name ?? ''} (${a?.id ?? ''})`,
                  }))}
                  issues={errors[`cast.${i}.allyId`]}
                  onChange={(v) => put({ ...r, allyId: v ?? '' })}
                />
              )}
              <div className="sm:col-span-3">
                <IssueList issues={errors[`cast.${i}.exclude`]} />
              </div>
            </div>
          )}
        />
      </Fieldset>
      <Fieldset legend="Hidden variables (rolled at issue)">
        <ListEditor
          label="Hidden vars"
          items={chain.hidden ?? []}
          onChange={(v) => set({ hidden: v })}
          what="hidden var"
          make={(): HiddenVarDef => ({
            id: '',
            type: 'bool',
            values: [],
            cases: [
              {
                else: true,
                pick: [
                  { w: 50, value: 'true' },
                  { w: 50, value: 'false' },
                ],
              },
            ],
          })}
          row={(h, i, put) => (
            <HiddenVarEditor
              value={h}
              onChange={put}
              errors={relErrors(errors, `hidden.${i}`)}
              roles={roles}
            />
          )}
        />
      </Fieldset>
      <Fieldset legend="Tells (hints rolled from the truth)">
        <ListEditor
          label="Tells"
          items={chain.tells ?? []}
          onChange={(v) => set({ tells: v })}
          what="tell"
          make={(): TellDef => ({
            id: 'tell.',
            of: hiddenIds[0] ?? '',
            p: { true: 0.75, false: 0.2 },
          })}
          row={(t, i, put) => {
            const parent = (chain.hidden ?? []).find((h) => h.id === t.of);
            const keys = parent?.type === 'enum' ? (parent.values ?? []) : ['true', 'false'];
            return (
              <div className="grid gap-1 sm:grid-cols-3">
                <TextField
                  label="Id"
                  mono
                  value={t.id}
                  issues={errors[`tells.${i}.id`]}
                  onChange={(v) => put({ ...t, id: v })}
                />
                <SelectField
                  label="Hints at"
                  value={t.of ?? ''}
                  options={opts(hiddenIds)}
                  issues={errors[`tells.${i}.of`]}
                  onChange={(v) => put({ ...t, of: v ?? '' })}
                />
                <div className="flex flex-wrap gap-1">
                  {keys.map((k) => (
                    <label key={k} className="text-xs">
                      P(shown | {k})
                      <NumberInput
                        label={`P shown if ${k}`}
                        min={0}
                        max={1}
                        step={0.05}
                        value={t.p?.[k] ?? 0}
                        onChange={(v) => put({ ...t, p: { ...(t.p ?? {}), [k]: v ?? 0 } })}
                      />
                    </label>
                  ))}
                </div>
                <div className="sm:col-span-3">
                  <IssueList issues={errors[`tells.${i}.p`]} />
                </div>
              </div>
            );
          }}
        />
      </Fieldset>
    </>
  );
}

function HiddenVarEditor({
  value: h,
  onChange,
  errors,
  roles,
}: {
  value: HiddenVarDef;
  onChange: (v: HiddenVarDef) => void;
  errors: FieldErrors;
  roles: string[];
}) {
  const put = (patch: Partial<HiddenVarDef>) => onChange({ ...h, ...patch });
  return (
    <div className="grid gap-1 sm:grid-cols-3">
      <TextField label="Id" mono value={h.id} issues={errors.id} onChange={(v) => put({ id: v })} />
      <SelectField
        label="Type"
        value={h.type ?? 'bool'}
        options={opts(HIDDEN_VAR_TYPES)}
        issues={errors.type}
        onChange={(v) => put({ type: v ?? 'bool' })}
      />
      {h.type === 'enum' && (
        <TextField
          label="Values (comma)"
          mono
          value={(h.values ?? []).join(', ')}
          issues={errors.values}
          onChange={(v) =>
            put({
              values: v
                .split(',')
                .map((x) => x.trim())
                .filter(Boolean),
            })
          }
        />
      )}
      <div className="sm:col-span-3">
        <ListEditor
          label="Cases (first that holds, then a weighted pick; the last is else)"
          items={h.cases ?? []}
          onChange={(v) => put({ cases: v })}
          what="case"
          issues={errors.cases}
          make={() => ({ if: null, else: false, pick: [{ w: 1, value: '' }] })}
          row={(k, n, putCase) => (
            <div>
              <CheckboxField
                label="else"
                checked={!!k.else}
                onChange={(v) => putCase({ ...k, else: v, if: v ? null : k.if })}
              />
              {!k.else && (
                <ConditionPicker
                  label={`Case ${n + 1} if`}
                  value={k.if}
                  onChange={(c) => putCase({ ...k, if: c })}
                  hints={{ roles, allowRaid: false }}
                  issues={relErrors(errors, `cases.${n}.if`)}
                />
              )}
              <ListEditor
                label="Pick (weight → value)"
                items={k.pick ?? []}
                onChange={(v) => putCase({ ...k, pick: v })}
                what="pick"
                issues={errors[`cases.${n}.pick`]}
                make={() => ({ w: 1, value: '' })}
                row={(p, q, putPick) => (
                  <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-1">
                    <NumberInput
                      label="Weight"
                      integer
                      min={0}
                      value={p.w}
                      onChange={(v) => putPick({ ...p, w: v ?? 0 })}
                    />
                    <input
                      aria-label="Value"
                      className={`${inputCls} font-mono`}
                      value={p.value ?? ''}
                      onChange={(e) => putPick({ ...p, value: e.target.value })}
                    />
                    <div className="col-span-2">
                      <IssueList issues={errors[`cases.${n}.pick.${q}.value`]} />
                    </div>
                  </div>
                )}
              />
            </div>
          )}
        />
      </div>
    </div>
  );
}

function StringsEditor({
  chain,
  onChange,
  roles,
  errors,
}: {
  chain: BeatChainDef;
  onChange: (c: BeatChainDef) => void;
  roles: Set<string>;
  errors: FieldErrors;
}) {
  const entries = Object.entries(chain.strings ?? {});
  const [newKey, setNewKey] = useState('');
  const put = (k: string, v: string) =>
    onChange({ ...chain, strings: { ...(chain.strings ?? {}), [k]: v } });
  return (
    <Fieldset legend="Strings (English source; captions ≤ 120, balloons ≤ 90 characters)">
      {entries.length === 0 && (
        <p className="text-xs text-bruise sm:col-span-2">
          No strings yet. Cells add theirs when you type a caption or balloon.
        </p>
      )}
      {entries.map(([k, v]) => (
        <div key={k} className="sm:col-span-2">
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <TemplateInput label={k} value={v} onChange={(nv) => put(k, nv)} roles={roles} />
            </div>
            <button
              type="button"
              className={`${smallBtn} text-blam`}
              aria-label={`Remove string ${k}`}
              onClick={() => {
                const next = { ...(chain.strings ?? {}) };
                delete next[k];
                onChange({ ...chain, strings: next });
              }}
            >
              ✕
            </button>
          </div>
          <IssueList issues={errors[`strings.${k}`]} />
        </div>
      ))}
      <div className="flex items-end gap-2 sm:col-span-2">
        <input
          aria-label="New string key"
          className={`${inputCls} font-mono`}
          placeholder="nmb.c3.cap"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
        />
        <button
          type="button"
          className={smallBtn}
          disabled={!newKey || newKey in (chain.strings ?? {})}
          onClick={() => {
            put(newKey, '');
            setNewKey('');
          }}
        >
          Add string
        </button>
      </div>
    </Fieldset>
  );
}

function CellsEditor({
  cells,
  onChange,
  base,
  errors,
  cellCtx,
  vars,
}: {
  cells: CellDef[];
  onChange: (c: CellDef[]) => void;
  base: string;
  errors: FieldErrors;
  cellCtx: CellContext;
  vars: string[];
}) {
  return (
    <ListEditor
      label="Cells (panel specs)"
      items={cells}
      onChange={onChange}
      what="cell"
      issues={errors[base]}
      make={() => ({
        panel: newPanel(`${base.split('.').slice(-2, -1)[0] ?? 'c'}.c${cells.length + 1}`),
        showIf: null,
      })}
      row={(c, i, put) => (
        <details>
          <summary className="cursor-pointer text-xs">
            <span className="font-mono">{c.panel?.id || `cell ${i + 1}`}</span> ·{' '}
            {c.panel?.shot?.type ?? 'medium'} {c.panel?.shot?.subject ?? ''} ·{' '}
            {(c.panel?.actors ?? []).map((a) => `${a.role}:${a.pose}`).join(', ')}
            {c.showIf ? ` [showIf ${condText(c.showIf)}]` : ''}
            {issuesUnder(errors, `${base}.${i}`).some((x) => x.severity === 'error') && (
              <span className="ml-1 font-semibold text-blam">●</span>
            )}
          </summary>
          <ConditionPicker
            label="Show if"
            value={c.showIf}
            onChange={(v) => put({ ...c, showIf: v })}
            hints={{ vars, allowRaid: false }}
            issues={relErrors(errors, `${base}.${i}.showIf`)}
          />
          <CellEditor
            value={c.panel ?? newPanel(`c${i + 1}`)}
            onChange={(p) => put({ ...c, panel: p })}
            ctx={cellCtx}
            errors={errors}
            base={`${base}.${i}.panel`}
          />
        </details>
      )}
    />
  );
}

function BeatEditor({
  id,
  beat,
  chain,
  onChange,
  onRename,
  onRoot,
  onDelete,
  onAddOption,
  onRemoveOption,
  onSelect,
  errors,
  cellCtx,
  roles,
  vars,
}: {
  id: string;
  beat: BeatDef;
  chain: BeatChainDef;
  onChange: (b: BeatDef) => void;
  onRename: (to: string) => boolean;
  onRoot: () => void;
  onDelete: () => void;
  onAddOption: () => void;
  onRemoveOption: (i: number) => void;
  onSelect: (s: Sel) => void;
  errors: FieldErrors;
  cellCtx: CellContext;
  roles: string[];
  vars: string[];
}) {
  const base = `beats.${id}`;
  const choice = beat.choice;
  const roleSet = new Set(roles);
  return (
    <>
      <Fieldset legend={`Beat ${id}`}>
        <RenameField label="Id" id={id} onRename={onRename} />
        <div className="flex flex-wrap items-end gap-2">
          {chain.root !== id && (
            <button type="button" className={smallBtn} onClick={onRoot}>
              Make root
            </button>
          )}
          <button type="button" className={`${smallBtn} text-blam`} onClick={onDelete}>
            Delete beat
          </button>
        </div>
        <CheckboxField
          label="Has a choice (off: a cutscene beat)"
          checked={!!choice}
          onChange={(v) =>
            onChange(
              v
                ? {
                    ...beat,
                    choice: { prompt: '', timerS: null, timeout: '', options: [] },
                    outcome: '',
                  }
                : { ...beat, choice: null, outcome: Object.keys(chain.outcomes ?? {})[0] ?? '' },
            )
          }
        />
        <div className="sm:col-span-2">
          <IssueList issues={[...(errors[base] ?? []), ...(errors[`${base}.cells`] ?? [])]} />
        </div>
      </Fieldset>
      <Fieldset legend="Setup cells">
        <CellsEditor
          cells={beat.cells ?? []}
          onChange={(c) => onChange({ ...beat, cells: c })}
          base={`${base}.cells`}
          errors={errors}
          cellCtx={cellCtx}
          vars={vars}
        />
      </Fieldset>
      {choice ? (
        <Fieldset legend="Choice">
          <TemplateInput
            label="Prompt"
            value={choice.prompt ?? ''}
            onChange={(v) => onChange({ ...beat, choice: { ...choice, prompt: v } })}
            roles={roleSet}
            hint="{companion.name}'s shaking. You've got seconds."
          />
          <IssueList issues={errors[`${base}.choice.prompt`]} />
          <NumberField
            label="Timer s (3..20, empty = none)"
            integer
            nullable
            min={3}
            max={20}
            value={choice.timerS}
            issues={errors[`${base}.choice.timerS`]}
            onChange={(v) => onChange({ ...beat, choice: { ...choice, timerS: v } })}
          />
          <SelectField
            label="Timeout option"
            value={choice.timeout ?? ''}
            none="(none)"
            options={(choice.options ?? []).map((o) => ({
              value: o.id ?? '',
              label: `${o.id} ${o.label ?? ''}`,
            }))}
            issues={errors[`${base}.choice.timeout`]}
            onChange={(v) => onChange({ ...beat, choice: { ...choice, timeout: v ?? '' } })}
          />
          <div className="sm:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide">Options (2–3)</span>
            <ul className="mt-1 space-y-1">
              {(choice.options ?? []).map((o, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 rounded border border-newsprint p-1 text-xs"
                >
                  <span className="font-mono font-bold">{o.id}</span>
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  <span className="font-mono text-bruise">→ {o.table}</span>
                  {o.requires && (
                    <span className="text-bruise">requires {condText(o.requires)}</span>
                  )}
                  {issuesUnder(errors, `${base}.choice.options.${i}`).some(
                    (x) => x.severity === 'error',
                  ) && <span className="font-semibold text-blam">●</span>}
                  <button
                    type="button"
                    className={smallBtn}
                    onClick={() => onSelect({ kind: 'option', beat: id, index: i })}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={`${smallBtn} text-blam`}
                    aria-label={`Remove option ${o.id}`}
                    onClick={() => onRemoveOption(i)}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className={`${smallBtn} mt-1`}
              disabled={(choice.options ?? []).length >= 3}
              onClick={onAddOption}
            >
              Add option
            </button>
            <IssueList issues={errors[`${base}.choice.options`]} />
          </div>
        </Fieldset>
      ) : (
        <Fieldset legend="Cutscene">
          <SelectField
            label="Outcome"
            value={beat.outcome ?? ''}
            options={opts(Object.keys(chain.outcomes ?? {}))}
            issues={errors[`${base}.outcome`]}
            onChange={(v) => onChange({ ...beat, outcome: v ?? '' })}
          />
        </Fieldset>
      )}
    </>
  );
}

function OptionEditor({
  option: o,
  base,
  chain,
  onChange,
  onNewTable,
  errors,
  roles,
  vars,
}: {
  option: OptionDef;
  base: string;
  chain: BeatChainDef;
  onChange: (o: OptionDef) => void;
  onNewTable: () => void;
  errors: FieldErrors;
  roles: string[];
  vars: string[];
}) {
  const put = (patch: Partial<OptionDef>) => onChange({ ...o, ...patch });
  return (
    <Fieldset legend={`Option ${o.id ?? ''}`}>
      <TextField
        label="Id"
        mono
        value={o.id}
        issues={errors[`${base}.id`]}
        onChange={(v) => put({ id: v })}
      />
      <div className="flex items-end gap-1">
        <div className="min-w-0 flex-1">
          <SelectField
            label="Table"
            value={o.table ?? ''}
            options={opts(Object.keys(chain.tables ?? {}))}
            issues={errors[`${base}.table`]}
            onChange={(v) => put({ table: v ?? '' })}
          />
        </div>
        <button type="button" className={smallBtn} onClick={onNewTable}>
          New table
        </button>
      </div>
      <TemplateInput
        label="Label"
        value={o.label ?? ''}
        onChange={(v) => put({ label: v })}
        roles={new Set(roles)}
      />
      <IssueList issues={errors[`${base}.label`]} />
      <ConditionPicker
        label="Requires (usually a raid fact; else crossed out)"
        value={o.requires}
        onChange={(c) => put({ requires: c })}
        hints={{ vars, roles, allowRaid: true }}
        issues={relErrors(errors, `${base}.requires`)}
      />
      {o.requires && (
        <TextField
          label="Requires label"
          value={o.requiresLabel}
          issues={errors[`${base}.requiresLabel`]}
          onChange={(v) => put({ requiresLabel: v })}
          hint="Shown on the crossed-out option: Nothing she wants."
        />
      )}
      <TextField
        label="Tone note"
        value={o.toneNote}
        issues={errors[`${base}.toneNote`]}
        onChange={(v) => put({ toneNote: v })}
        hint="Why an option with no good or windfall path is fine (a mercy option, a pure escape)."
      />
    </Fieldset>
  );
}

function TableEditor({
  id,
  chain,
  onChange,
  onAddCase,
  onNewOutcome,
  onRename,
  onDelete,
  onSelectOutcome,
  errors,
  roles,
  vars,
}: {
  id: string;
  chain: BeatChainDef;
  onChange: (t: OutcomeTableCase[]) => void;
  onAddCase: () => void;
  onNewOutcome: (caseIdx: number, pickIdx: number) => void;
  onRename: (to: string) => boolean;
  onDelete?: () => void;
  onSelectOutcome: (id: string) => void;
  errors: FieldErrors;
  roles: string[];
  vars: string[];
}) {
  const cases = chain.tables?.[id] ?? [];
  const base = `tables.${id}`;
  const outcomes = Object.keys(chain.outcomes ?? {});
  return (
    <Fieldset legend={`Table ${id} (first matching case, then a weighted pick)`}>
      <RenameField label="Table id" id={id} onRename={onRename} />
      {onDelete && (
        <div className="flex items-end">
          <button type="button" className={`${smallBtn} text-blam`} onClick={onDelete}>
            Delete table
          </button>
        </div>
      )}
      <ListEditor
        label="Cases (reorder with the arrows; the last is else)"
        items={cases}
        onChange={onChange}
        what="case"
        issues={errors[base]}
        make={() => ({ if: null, else: false, pick: [{ w: 1, outcome: outcomes[0] ?? '' }] })}
        row={(k, n, put) => (
          <div>
            <CheckboxField
              label="else"
              checked={!!k.else}
              onChange={(v) => put({ ...k, else: v, if: v ? null : k.if })}
            />
            {!k.else && (
              <ConditionPicker
                label={`Case ${n + 1} if`}
                value={k.if}
                onChange={(c) => put({ ...k, if: c })}
                hints={{ vars, roles, allowRaid: true }}
                issues={relErrors(errors, `${base}.${n}.if`)}
              />
            )}
            <ListEditor
              label="Pick (weight → outcome)"
              items={k.pick ?? []}
              onChange={(v) => put({ ...k, pick: v })}
              what="pick"
              issues={errors[`${base}.${n}.pick`]}
              make={() => ({ w: 1, outcome: outcomes[0] ?? '' })}
              row={(p, q, putPick) => (
                <div className="grid grid-cols-[5rem_minmax(0,1fr)_auto_auto] items-start gap-1">
                  <NumberInput
                    label="Weight"
                    integer
                    min={0}
                    value={p.w}
                    onChange={(v) => putPick({ ...p, w: v ?? 0 })}
                  />
                  <SelectInput
                    label="Outcome"
                    value={p.outcome ?? ''}
                    options={opts(outcomes)}
                    onChange={(v) => putPick({ ...p, outcome: v ?? '' })}
                  />
                  <button type="button" className={smallBtn} onClick={() => onNewOutcome(n, q)}>
                    New
                  </button>
                  <button
                    type="button"
                    className={smallBtn}
                    disabled={!p.outcome || !chain.outcomes?.[p.outcome]}
                    onClick={() => onSelectOutcome(p.outcome ?? '')}
                  >
                    Edit
                  </button>
                  <div className="col-span-4">
                    <IssueList issues={errors[`${base}.${n}.pick.${q}.outcome`]} />
                  </div>
                </div>
              )}
            />
            <IssueList issues={errors[`${base}.${n}`]} />
          </div>
        )}
      />
      <button type="button" className={smallBtn} onClick={onAddCase}>
        Add case (before the else)
      </button>
    </Fieldset>
  );
}

function OutcomeEditor({
  id,
  outcome: o,
  chain,
  onChange,
  onRename,
  onDelete,
  canAddBeat,
  onAddNextBeat,
  onSelectBeat,
  errors,
  cellCtx,
  roles,
  maxDepth,
}: {
  id: string;
  outcome: OutcomeDef;
  chain: BeatChainDef;
  onChange: (o: OutcomeDef) => void;
  onRename: (to: string) => boolean;
  onDelete: () => void;
  canAddBeat: boolean;
  onAddNextBeat: () => void;
  onSelectBeat: (id: string) => void;
  errors: FieldErrors;
  cellCtx: CellContext;
  roles: string[];
  maxDepth: number;
}) {
  const base = `outcomes.${id}`;
  const put = (patch: Partial<OutcomeDef>) => onChange({ ...o, ...patch });
  const hidden = (chain.hidden ?? []).map((h) => h.id ?? '');
  const tone = (o.tone ?? 'mixed') as keyof typeof TONE_COLORS;
  return (
    <>
      <Fieldset legend={`Outcome ${id}`}>
        <RenameField label="Id" id={id} onRename={onRename} />
        <div className="flex items-end gap-2">
          <span
            className="rounded px-2 py-1 text-xs font-semibold"
            style={{
              background: TONE_COLORS[tone] ?? '#fff',
              color: tone === 'disaster' || tone === 'bad' ? '#fff' : '#15121a',
            }}
          >
            {o.tone ?? 'mixed'}
          </span>
          <button type="button" className={`${smallBtn} text-blam`} onClick={onDelete}>
            Delete outcome
          </button>
        </div>
        <SelectField
          label="Tone"
          value={o.tone ?? 'mixed'}
          options={opts(OUTCOME_TONES)}
          issues={errors[`${base}.tone`]}
          onChange={(v) => put({ tone: v ?? 'mixed' })}
          hint="Windfall cells get the gold WINDFALL stamp."
        />
        <div className="flex items-end gap-1">
          <div className="min-w-0 flex-1">
            <SelectField
              label="Next beat"
              value={o.next ?? ''}
              none="(ends here)"
              options={opts(Object.keys(chain.beats ?? {}))}
              issues={errors[`${base}.next`]}
              onChange={(v) => put({ next: v ?? '' })}
            />
          </div>
          {o.next ? (
            <button type="button" className={smallBtn} onClick={() => onSelectBeat(o.next!)}>
              Go
            </button>
          ) : (
            <button
              type="button"
              className={smallBtn}
              disabled={!canAddBeat}
              title={canAddBeat ? undefined : `Paths hold at most ${maxDepth} choices.`}
              onClick={onAddNextBeat}
            >
              New beat
            </button>
          )}
        </div>
        <StringListEditor
          label="Ledger tags"
          value={o.tags}
          onChange={(v) => put({ tags: v })}
          errors={errors}
          base={`${base}.tags`}
          hint="trusted, killed_companion… read later by ledger.tag."
        />
        <div className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-wide">
            Reveals (for the debrief)
          </span>
          <div className="flex flex-wrap gap-1">
            {hidden.map((h) => {
              const on = (o.reveals ?? []).includes(h);
              return (
                <button
                  key={h}
                  type="button"
                  aria-pressed={on}
                  className={`rounded border px-1.5 text-xs ${on ? 'border-ink bg-benday/40' : 'border-newsprint'}`}
                  onClick={() =>
                    put({
                      reveals: on
                        ? (o.reveals ?? []).filter((x) => x !== h)
                        : [...(o.reveals ?? []), h],
                    })
                  }
                >
                  {h}
                </button>
              );
            })}
            {!hidden.length && <span className="text-xs text-bruise">No hidden vars.</span>}
          </div>
          <IssueList issues={issuesUnder(errors, `${base}.reveals`)} />
        </div>
      </Fieldset>
      <Fieldset legend="Consequence cells">
        <CellsEditor
          cells={o.cells ?? []}
          onChange={(c) => put({ cells: c })}
          base={`${base}.cells`}
          errors={errors}
          cellCtx={cellCtx}
          vars={cellCtx.vars}
        />
      </Fieldset>
      <Fieldset
        legend={`Effects (≤ 8) · ${(o.effects ?? []).map(effectSummary).join(' · ') || 'none'}`}
      >
        <EffectListEditor
          label="Effects"
          value={o.effects ?? []}
          onChange={(v) => put({ effects: v })}
          roles={roles}
          errors={errors}
          base={`${base}.effects`}
        />
      </Fieldset>
    </>
  );
}
